/**
 * useCountService — React port of countService (387 LOC)
 *
 * AngularJS source: services/countService.js
 *
 * Manages audit failure counts for the sidebar medallion system:
 * - Question failure counts (from audit results)
 * - Task failure counts (from task items)
 * - Billing failure counts (from task billing alerts)
 * - Damage counts (from ListOrderCounts API)
 * - Bid counts (from ListOrderCounts API)
 *
 * Each count type has a "bypass" system based on permission rules
 * that match loanType|clientCode|saleFlag|workCode patterns.
 *
 * This is implemented as a Zustand slice that extends useAuditStore.
 */

import { create } from 'zustand';
import { axiosClient } from '@/lib/axiosClient';
import { buildApiBase } from '@/lib/buildApiBase';
// NOTE: `useRctStore` was dropped from this import because its only use here —
// the disabled CompleteContext>=15 clause in refreshQuestionCountsV2 — is
// commented out. Re-add it to this import if that clause is ever restored.
import { useAuthStore } from '@/store';
import { usePropertyStore } from '@/store/propertyStore';
import type { AuditResult, AuditQuestion } from './auditApi';
import { useAuditStore, type TaskItemCategory } from './useAuditStore';

const API_BASE = `${buildApiBase()}/API`;

// ============ TYPES ============

interface OrderCount {
  group: string;
  count: number;
  audited: boolean;
}

interface OrderModule {
  orderModule: number; // 1 = damages, 2 = bids
  orderCounts: OrderCount[];
}

interface CategoryCount {
  categoryName: string;
  count: number;
}

interface DamageCount {
  category: string;
  count: number;
  audited: boolean;
}

interface BidCount {
  category: string;
  count: number;
  audited: boolean;
}

interface TaskCount {
  categoryName: string;
  count: number;
  requiresAudit: boolean;
}

interface GProperty {
  loanType?: string;
  clientCode?: { code?: string };
  saleDate?: string | null;
}

interface GWorkOrder {
  orderNumber?: number;
  workCode?: string;
  department?: string;
}

export interface CountServiceState {
  // True once any refresh* action has run — used by AppSidebar to keep the
  // Complete Audit button disabled during the gap between Zustand store reset
  // (hard page reload defaults totalCount to 0) and getResultsList resolving.
  // Without this gate the legacy ng-disabled clause `totalCount > 0` reads 0
  // for the first paint and the button briefly looks enabled mid-load.
  countsLoaded: boolean;

  // Failure counts
  bidFailureCount: number;
  damageFailureCount: number;
  questionFailureCount: number;
  newAuditFailureCount: number;
  taskFailureCount: number;
  billingFailureCount: number;
  totalCount: number;
  totalCountWithOutNewAudit: number;

  // Follow-up state
  onFollowupInspection: boolean;
  onFollowupInspectionAllAnsweredActionTaken: boolean;

  // Category counts (displayed in overview tables)
  questionCountsByCategory: CategoryCount[];
  taskCountsByCategory: TaskCount[];
  bidCountsByCategory: BidCount[];
  damageCountsByCategory: DamageCount[];

  // Bypass flags
  bypassAuditBids: boolean;
  bypassAuditDamages: boolean;
  bypassAuditTasks: boolean;
  bypassAuditBilling: boolean;
  bypassAuditQuestions: boolean;

  // Actions
  refreshDamageAndBidCounts: (orderNumber: number) => Promise<void>;
  refreshQuestionCounts: (auditResults: AuditResult[]) => void;
  refreshQuestionCountsV2: (coreScript: {
    Complete: boolean;
    CompleteContext: number;
    Followup: boolean;
  }) => void;
  refreshTaskCounts: (taskItems: TaskItemCategory[]) => void;
  refreshTaskCountsOnAccept: (taskItems: TaskItemCategory[]) => void;
  checkIfOnFollowup: (
    taskItems: TaskItemCategory[],
    auditResults: AuditResult[],
    fromNewAudit?: boolean,
    coreScript?: { Followup?: boolean }
  ) => boolean;
  checkIfAllAnsweredQuestionActionTaken: (auditResults: AuditResult[]) => boolean;
  checkIfRCTCalled: (auditResults: AuditResult[]) => boolean;
  recalculateTotalCount: () => void;
}

// ============ BYPASS RULES ============

function applyRules(list: string[]): boolean {
  const storeState = usePropertyStore.getState();
  const gProperty = storeState.property as GProperty | null;
  const gWorkOrder = storeState.workOrder as GWorkOrder | null;

  if (!gProperty || !gWorkOrder) return false;

  const loanType = gProperty.loanType || '';
  const clientCode = gProperty.clientCode?.code || '';
  const saleFlag = gProperty.saleDate != null ? 'POSTSALE' : 'PRESALE';
  const workCode = gWorkOrder.workCode || '';

  for (let i = 0; i < list.length; i++) {
    const bypassAudit = list[i];
    const value = bypassAudit.split('|');
    let newBypassAudit = value[0];

    for (let j = 1; j < value.length; j++) {
      if (j === 1) {
        newBypassAudit += value[j] === '*' ? '|*' : '|' + loanType;
      } else if (j === 2) {
        newBypassAudit += value[j] === '*' ? '|*' : '|' + clientCode;
      } else if (j === 3) {
        newBypassAudit += value[j] === '*' ? '|*' : '|' + saleFlag;
      } else if (j === 4) {
        newBypassAudit += value[j] === '*' ? '|*' : '|' + workCode;
      }
    }

    if (bypassAudit === newBypassAudit) {
      return true;
    }
  }
  return false;
}

function checkBypass(permKey: string): boolean {
  const permissions = useAuthStore.getState().permissions;
  // Check direct permission first
  const directBypass = Boolean(permissions?.[permKey] ?? false);
  if (directBypass) return true;

  // Check list-based bypass rules
  const list = permissions?.[permKey + 'List'] as string[] | undefined;
  if (list && Array.isArray(list)) {
    return applyRules(list);
  }
  return false;
}

// ============ FOLLOWUP HELPERS ============

/**
 * Computes `onFollowupInspection` + `onFollowupInspectionAllAnsweredActionTaken`
 * — matches AngularJS countService (deleted/public/SPIGlassApp/services/
 * countService.js) lines 189-232 + 274-276:
 *
 *   onFollowupInspection = checkIfOnFollowup() && dept === 'F'
 *   onFollowupInspectionAllAnsweredActionTaken
 *     = onFollowupInspection && checkIfAllAnsweredQuestionActionTaken()
 *
 * `checkIfAllAnsweredQuestionActionTaken` defaults to `true` and only flips
 * `false` when a question has audit failures AND is answered AND has no action
 * — same conservative default as AngularJS, so a follow-up order with no
 * unactioned answered failures correctly reports the flag as `true`.
 */
function computeFollowupFlags(
  taskItems: TaskItemCategory[],
  auditResults: AuditResult[]
): { onFollowupInspection: boolean; onFollowupInspectionAllAnsweredActionTaken: boolean } {
  // checkIfOnFollowup — countService.js:189-211
  let onFollowup = false;

  (taskItems || []).forEach((taskGroup) => {
    (taskGroup.tasks || []).forEach((task) => {
      const st = (task as unknown as Record<string, unknown>).stateTransition as
        | { name?: string }
        | undefined;
      if (st?.name === 'Follow-Up') onFollowup = true;
    });
  });

  (auditResults || []).forEach((result) => {
    (((result as Record<string, unknown>).questions as AuditQuestion[]) || []).forEach(
      (question) => {
        const selectedAction = (question as Record<string, unknown>).selectedAction as
          | Record<string, unknown>
          | undefined;
        if (selectedAction?.action && String(selectedAction.action).toLowerCase() === 'followup') {
          onFollowup = true;
        }
      }
    );
  });

  const storeWorkOrder = usePropertyStore.getState().workOrder as { department?: string } | null;
  const department = (storeWorkOrder?.department || '').toUpperCase();
  const onFollowupInspection = onFollowup && department === 'F';

  // checkIfAllAnsweredQuestionActionTaken — countService.js:214-232
  let allActionTaken = true;
  (auditResults || []).forEach((result) => {
    (((result as Record<string, unknown>).questions as AuditQuestion[]) || []).forEach(
      (question) => {
        const selectedAction = (question as Record<string, unknown>).selectedAction as
          | Record<string, unknown>
          | undefined;
        if (
          question.auditFailures &&
          question.auditFailures.length > 0 &&
          (!selectedAction || !selectedAction.actionText)
        ) {
          const reportedAnswers = (question as Record<string, unknown>).reportedAnswers as
            | { value?: unknown; key?: unknown }[]
            | undefined;
          if (
            reportedAnswers &&
            reportedAnswers.length > 0 &&
            (reportedAnswers[0].value != null || reportedAnswers[0].key != null)
          ) {
            allActionTaken = false;
          }
        }
      }
    );
  });

  return {
    onFollowupInspection,
    onFollowupInspectionAllAnsweredActionTaken: onFollowupInspection && allActionTaken,
  };
}

// ============ STORE ============

export const useCountService = create<CountServiceState>((set, get) => ({
  countsLoaded: false,
  bidFailureCount: 0,
  damageFailureCount: 0,
  questionFailureCount: 0,
  newAuditFailureCount: 0,
  taskFailureCount: 0,
  billingFailureCount: 0,
  totalCount: 0,
  totalCountWithOutNewAudit: 0,

  onFollowupInspection: false,
  onFollowupInspectionAllAnsweredActionTaken: false,

  questionCountsByCategory: [],
  taskCountsByCategory: [],
  bidCountsByCategory: [],
  damageCountsByCategory: [],

  bypassAuditBids: false,
  bypassAuditDamages: false,
  bypassAuditTasks: false,
  bypassAuditBilling: false,
  bypassAuditQuestions: false,

  // refreshDamageAndBidCounts — matches this.refreshDamageAndBidCounts
  refreshDamageAndBidCounts: async (orderNumber: number) => {
    if (!orderNumber) return;

    const response = await axiosClient.get<OrderModule[]>(
      `${API_BASE}/WorkOrderInfo/ListOrderCounts/`,
      { params: { OrderNumber: orderNumber } }
    );

    const modules = response.data;
    let bidFailureCount = 0;
    let damageFailureCount = 0;
    const bidCountsByCategory: BidCount[] = [];
    const damageCountsByCategory: DamageCount[] = [];

    for (let i = 0; i < modules.length; i++) {
      if (modules[i].orderModule === 1) {
        // Damages
        for (const oc of modules[i].orderCounts) {
          damageCountsByCategory.push({
            category: oc.group,
            count: oc.count,
            audited: oc.audited,
          });
          const bypassDamages = checkBypass('bypassAuditDamages');
          if (!bypassDamages && !oc.audited) {
            damageFailureCount += oc.count;
          }
        }
      }
      if (modules[i].orderModule === 2) {
        // Bids
        for (const oc of modules[i].orderCounts) {
          bidCountsByCategory.push({
            category: oc.group,
            count: oc.count,
            audited: oc.audited,
          });
          const bypassBids = checkBypass('bypassAuditBids');
          if (!bypassBids && !oc.audited) {
            bidFailureCount += oc.count;
          }
        }
      }
    }

    const state = get();
    const totalCount =
      bidFailureCount +
      damageFailureCount +
      state.questionFailureCount +
      state.taskFailureCount +
      state.billingFailureCount +
      state.newAuditFailureCount;
    // Same formula minus newAuditFailureCount. Backs the sidebar's
    // "Results Require Action" banner (AppSidebar.tsx:1412) whose legacy
    // countService.js:279 mistakenly doubled billing instead of excluding
    // newAudit. Kept as a separate field (not derived at render) so a stale
    // newAuditFailureCount can never leak into the banner mid-race.
    const totalCountWithOutNewAudit =
      bidFailureCount +
      damageFailureCount +
      state.questionFailureCount +
      state.taskFailureCount +
      state.billingFailureCount;

    set({
      bidFailureCount,
      damageFailureCount,
      bidCountsByCategory,
      damageCountsByCategory,
      totalCount,
      totalCountWithOutNewAudit,
    });
  },

  // refreshQuestionCounts — matches this.refreshQuestionCounts
  refreshQuestionCounts: (auditResults: AuditResult[]) => {
    let questionFailureCount = 0;
    const results: CategoryCount[] = [];

    const bypassQuestions = checkBypass('bypassAuditQuestions');

    (auditResults || []).forEach((category) => {
      const obj: CategoryCount = {
        categoryName: ((category as Record<string, unknown>).categoryName as string) || '',
        count: 0,
      };

      (((category as Record<string, unknown>).questions as AuditQuestion[]) || []).forEach(
        (question) => {
          if (
            question.auditFailures &&
            question.auditFailures.length > 0 &&
            (!(question as Record<string, unknown>).selectedAction ||
              !((question as Record<string, unknown>).selectedAction as Record<string, unknown>)
                ?.actionText)
          ) {
            obj.count++;
          }
        }
      );

      if (!bypassQuestions) {
        questionFailureCount += obj.count;
      }
      results.push(obj);
    });

    const state = get();
    const totalCount =
      state.bidFailureCount +
      state.damageFailureCount +
      questionFailureCount +
      state.taskFailureCount +
      state.billingFailureCount +
      state.newAuditFailureCount;
    const totalCountWithOutNewAudit =
      state.bidFailureCount +
      state.damageFailureCount +
      questionFailureCount +
      state.taskFailureCount +
      state.billingFailureCount;

    const followupFlags = computeFollowupFlags(
      useAuditStore.getState().taskItems || [],
      auditResults || []
    );

    set({
      questionFailureCount,
      questionCountsByCategory: results,
      totalCount,
      totalCountWithOutNewAudit,
      countsLoaded: true,
      ...followupFlags,
    });
  },

  // refreshQuestionCountsV2 — for new SafeView audit
  refreshQuestionCountsV2: (coreScript) => {
    let newAuditFailureCount = 0;
    // Only referenced by the CompleteContext>=15 clause below, which is disabled
    // (in Angular $rootScope.doNotShowRCT was undefined so that clause never
    // fired — dead code). Left commented so it can be restored alongside the
    // clause if we ever re-entertain that logic. See the block below.
    // const doNotShowRCT = useRctStore.getState().doNotShowRct;

    if (!coreScript.Complete && !coreScript.Followup) {
      newAuditFailureCount = 1;
    }

     // this is being removed because it actually breaks in react and in Angular doNotshowRCT was undefined
     // so this was never hit. dead code. If we want code around this later, we will need a new way
    /*
    if (coreScript.CompleteContext >= 15 && doNotShowRCT === false && !coreScript.Followup) {
          newAuditFailureCount = 1;
    }
     */


    const state = get();
    const totalCount =
      state.bidFailureCount +
      state.damageFailureCount +
      0 + // questionFailureCount = 0 for V2
      state.taskFailureCount +
      state.billingFailureCount +
      newAuditFailureCount;
    const totalCountWithOutNewAudit =
      state.bidFailureCount +
      state.damageFailureCount +
      0 + // questionFailureCount = 0 for V2
      state.taskFailureCount +
      state.billingFailureCount;

    set({
      questionFailureCount: 0,
      newAuditFailureCount,
      questionCountsByCategory: [],
      totalCount,
      totalCountWithOutNewAudit,
      countsLoaded: true,
    });
  },

  // refreshTaskCounts — matches this.refreshTaskCounts
  refreshTaskCounts: (taskItems: TaskItemCategory[]) => {
    let taskFailureCount = 0;
    let billingFailureCount = 0;
    const taskCountsByCategory: TaskCount[] = [];

    const bypassTasks = checkBypass('bypassAuditTasks');
    const bypassBilling = checkBypass('bypassAuditBilling');

    (taskItems || []).forEach((item) => {
      const obj: TaskCount = {
        categoryName: (item.externalName || '') + ' - Tasks',
        count: 0,
        requiresAudit: false,
      };
      let billingCount = 0;
      let groupRequiresAudit = false;

      (item.tasks || []).forEach((task) => {
        let requiresAudit = false;
        let billingRequiresAudit = false;

        if ((item as unknown as Record<string, unknown>).allowSpiAudit === false) {
          requiresAudit = true;
          groupRequiresAudit = true;
        } else if (
          (task as Record<string, unknown>).billingAlerts &&
          Array.isArray((task as Record<string, unknown>).billingAlerts) &&
          ((task as Record<string, unknown>).billingAlerts as { errorFlag: boolean }[]).length > 0
        ) {
          ((task as Record<string, unknown>).billingAlerts as { errorFlag: boolean }[]).forEach(
            (alert) => {
              if (alert.errorFlag) {
                groupRequiresAudit = true;
                billingRequiresAudit = true;
              }
            }
          );
        }

        if (requiresAudit) obj.count++;
        if (billingRequiresAudit) billingCount++;
      });

      obj.requiresAudit = groupRequiresAudit;

      if (!bypassTasks) {
        taskFailureCount += obj.count;
      }
      obj.count += billingCount;
      taskCountsByCategory.push(obj);

      if (!bypassBilling) {
        billingFailureCount += billingCount;
      }
    });

    const state = get();
    const totalCount =
      state.bidFailureCount +
      state.damageFailureCount +
      state.questionFailureCount +
      taskFailureCount +
      billingFailureCount +
      state.newAuditFailureCount;
    const totalCountWithOutNewAudit =
      state.bidFailureCount +
      state.damageFailureCount +
      state.questionFailureCount +
      taskFailureCount +
      billingFailureCount;

    const followupFlags = computeFollowupFlags(
      taskItems || [],
      useAuditStore.getState().results || []
    );

    set({
      taskFailureCount,
      billingFailureCount,
      taskCountsByCategory,
      totalCount,
      totalCountWithOutNewAudit,
      countsLoaded: true,
      ...followupFlags,
    });
  },

  // refreshTaskCountsOnAccept
  refreshTaskCountsOnAccept: (taskItems: TaskItemCategory[]) => {
    let taskFailureCount = 0;
    const taskCountsByCategory: TaskCount[] = [];
    const bypassTasks = checkBypass('bypassAuditTasks');

    (taskItems || []).forEach((item) => {
      if (item.tasks.length > 0) {
        const obj: TaskCount = {
          categoryName: (item.externalName || '') + ' - Tasks',
          count: 0,
          requiresAudit: false,
        };
        let groupRequiresAudit = false;

        item.tasks.forEach((task) => {
          groupRequiresAudit = task.requiresAudit || false;
          if (task.requiresAudit) {
            obj.count++;
          }
        });

        obj.requiresAudit = groupRequiresAudit;
        taskCountsByCategory.push(obj);

        if (!bypassTasks) {
          taskFailureCount += obj.count;
        }
      }
    });

    const state = get();
    const totalCount =
      state.bidFailureCount +
      state.damageFailureCount +
      state.questionFailureCount +
      taskFailureCount +
      state.billingFailureCount +
      state.newAuditFailureCount;
    const totalCountWithOutNewAudit =
      state.bidFailureCount +
      state.damageFailureCount +
      state.questionFailureCount +
      taskFailureCount +
      state.billingFailureCount;

    set({ taskFailureCount, taskCountsByCategory, totalCount, totalCountWithOutNewAudit });
  },

  // checkIfOnFollowup
  checkIfOnFollowup: (taskItems, auditResults, fromNewAudit = false, coreScript) => {
    let onFollowup = false;

    taskItems.forEach((taskItem) => {
      taskItem.tasks.forEach((task) => {
        if (
          (task as Record<string, unknown>).stateTransition &&
          ((task as Record<string, unknown>).stateTransition as Record<string, unknown>).name ===
            'Follow-Up'
        ) {
          onFollowup = true;
        }
      });
    });

    if (fromNewAudit && coreScript?.Followup) {
      onFollowup = true;
    } else {
      auditResults.forEach((result) => {
        (((result as Record<string, unknown>).questions as AuditQuestion[]) || []).forEach(
          (question) => {
            const selectedAction = (question as Record<string, unknown>).selectedAction as
              | Record<string, unknown>
              | undefined;
            if (
              selectedAction?.action &&
              String(selectedAction.action).toLowerCase() === 'followup'
            ) {
              onFollowup = true;
            }
          }
        );
      });
    }

    return onFollowup;
  },

  // checkIfAllAnsweredQuestionActionTaken
  checkIfAllAnsweredQuestionActionTaken: (auditResults) => {
    let allActionTaken = true;

    auditResults.forEach((result) => {
      (((result as Record<string, unknown>).questions as AuditQuestion[]) || []).forEach(
        (question) => {
          const selectedAction = (question as Record<string, unknown>).selectedAction as
            | Record<string, unknown>
            | undefined;
          if (
            question.auditFailures &&
            question.auditFailures.length > 0 &&
            (!selectedAction || !selectedAction.actionText)
          ) {
            const reportedAnswers = (question as Record<string, unknown>).reportedAnswers as
              | { value?: unknown; key?: unknown }[]
              | undefined;
            if (
              reportedAnswers &&
              reportedAnswers.length > 0 &&
              (reportedAnswers[0].value != null || reportedAnswers[0].key != null)
            ) {
              allActionTaken = false;
            }
          }
        }
      );
    });

    return allActionTaken;
  },

  // checkIfRCTCalled
  // recalculateTotalCount — recomputes totalCount from all individual failure counts.
  // Must be called after all parallel refresh functions complete to avoid race conditions.
  recalculateTotalCount: () => {
    const state = get();
    const totalCount =
      state.bidFailureCount +
      state.damageFailureCount +
      state.questionFailureCount +
      state.taskFailureCount +
      state.billingFailureCount +
      state.newAuditFailureCount;
    const totalCountWithOutNewAudit =
      state.bidFailureCount +
      state.damageFailureCount +
      state.questionFailureCount +
      state.taskFailureCount +
      state.billingFailureCount;
    set({ totalCount, totalCountWithOutNewAudit });
  },

  checkIfRCTCalled: (auditResults) => {
    let rctCalled = false;
    const rctQuestion = 'WorkOrder.CoreLogic.RCT';

    auditResults.forEach((result) => {
      (((result as Record<string, unknown>).questions as AuditQuestion[]) || []).forEach(
        (question) => {
          if (question.questionKey === rctQuestion) {
            rctCalled = true;
          }
        }
      );
    });

    return rctCalled;
  },
}));
